import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccountSettings = ({ userData, onSettingsUpdate }) => {
  const [activeSection, setActiveSection] = useState('personal');
  const [personalData, setPersonalData] = useState({
    phone: userData?.phone || '',
    email: userData?.email || '',
    pixKey: userData?.pixKey || ''
  });
  const [notifications, setNotifications] = useState(userData?.notifications || {});
  const [privacy, setPrivacy] = useState(userData?.privacy || {});
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 1,
      type: 'pix',
      name: 'PIX',
      identifier: userData?.pixKey || '',
      isDefault: true
    },
    {
      id: 2,
      type: 'card',
      name: 'Cartão de Crédito',
      identifier: '**** **** **** 1234',
      isDefault: false
    }
  ]);

  const sections = [
    { id: 'personal', label: 'Pessoal', icon: 'User' },
    { id: 'notifications', label: 'Notificações', icon: 'Bell' },
    { id: 'privacy', label: 'Privacidade', icon: 'Shield' },
    { id: 'payment', label: 'Pagamento', icon: 'CreditCard' }
  ];

  const handlePersonalDataChange = (field, value) => {
    setPersonalData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotificationToggle = (setting) => {
    setNotifications(prev => {
      const updated = { ...prev, [setting]: !prev?.[setting] };
      onSettingsUpdate({ notifications: updated });
      return updated;
    });
  };

  const handlePrivacyToggle = (setting) => {
    setPrivacy(prev => {
      const updated = { ...prev, [setting]: !prev?.[setting] };
      onSettingsUpdate({ privacy: updated });
      return updated;
    });
  };

  const handlePersonalDataSave = () => {
    onSettingsUpdate({ 
      phone: personalData?.phone,
      email: personalData?.email,
      pixKey: personalData?.pixKey
    });
  };

  const handleSetDefaultPayment = (paymentId) => {
    setPaymentMethods(prev => 
      prev?.map(method => ({ 
        ...method, 
        isDefault: method?.id === paymentId 
      }))
    );
  };

  const getPaymentIcon = (type) => {
    switch (type) {
      case 'pix': return 'Smartphone';
      case 'card': return 'CreditCard';
      default: return 'Wallet';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <h2 className="font-heading font-bold text-lg text-card-foreground mb-3">
          Configurações da Conta
        </h2>
        
        {/* Section Tabs */}
        <div className="grid grid-cols-2 gap-1 bg-muted rounded-lg p-1">
          {sections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => setActiveSection(section?.id)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded transition-standard justify-center ${
                activeSection === section?.id
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={section?.icon} size={16} />
              <span className="hidden sm:inline">{section?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="max-h-80 overflow-y-auto">
        {/* Personal Information */}
        {activeSection === 'personal' && (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Telefone
              </label>
              <Input
                type="tel"
                placeholder="+55 (11) 99999-9999"
                value={personalData?.phone}
                onChange={(e) => handlePersonalDataChange('phone', e?.target?.value)}
                iconName="Phone"
                iconPosition="left"
                iconSize={16}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Email
              </label>
              <Input
                type="email"
                placeholder="seu@email.com"
                value={personalData?.email}
                onChange={(e) => handlePersonalDataChange('email', e?.target?.value)}
                iconName="Mail"
                iconPosition="left"
                iconSize={16}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-card-foreground mb-2">
                Chave PIX
              </label>
              <Input
                placeholder="Email, telefone, CPF ou chave aleatória"
                value={personalData?.pixKey}
                onChange={(e) => handlePersonalDataChange('pixKey', e?.target?.value)}
                iconName="Smartphone"
                iconPosition="left"
                iconSize={16}
              />
            </div>
            
            <div className="pt-2 border-t border-border">
              <Button
                variant="default"
                size="sm"
                onClick={handlePersonalDataSave}
                iconName="Save"
                iconPosition="left"
                iconSize={16}
                fullWidth
              >
                Salvar Alterações
              </Button>
            </div>
            
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-destructive text-sm">Zona de Perigo</h4>
                  <p className="text-xs text-destructive/80 mt-1">
                    Ações irreversíveis que afetam permanentemente sua conta.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
                  >
                    Desativar Conta
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notifications */}
        {activeSection === 'notifications' && (
          <div className="p-4 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Peladas</h4>
                  <p className="text-xs text-muted-foreground">Convites, confirmações e lembretes</p>
                </div>
                <Checkbox
                  checked={notifications?.matches}
                  onChange={() => handleNotificationToggle('matches')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Pagamentos</h4>
                  <p className="text-xs text-muted-foreground">Cobranças, recebimentos e lembretes</p>
                </div>
                <Checkbox
                  checked={notifications?.payments}
                  onChange={() => handleNotificationToggle('payments')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Social</h4>
                  <p className="text-xs text-muted-foreground">Novos amigos, mensagens e atividades</p>
                </div>
                <Checkbox
                  checked={notifications?.social}
                  onChange={() => handleNotificationToggle('social')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Marketing</h4>
                  <p className="text-xs text-muted-foreground">Promoções, novidades e dicas</p>
                </div>
                <Checkbox
                  checked={notifications?.marketing}
                  onChange={() => handleNotificationToggle('marketing')}
                />
              </div>
            </div>
            
            <div className="pt-4 border-t border-border">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-card-foreground text-sm">Notificações Push</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Configure as notificações do navegador nas configurações do seu dispositivo.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Privacy */}
        {activeSection === 'privacy' && (
          <div className="p-4 space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Mostrar telefone</h4>
                  <p className="text-xs text-muted-foreground">Outros usuários podem ver seu telefone</p>
                </div>
                <Checkbox
                  checked={privacy?.showPhone}
                  onChange={() => handlePrivacyToggle('showPhone')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Mostrar email</h4>
                  <p className="text-xs text-muted-foreground">Outros usuários podem ver seu email</p>
                </div>
                <Checkbox
                  checked={privacy?.showEmail}
                  onChange={() => handlePrivacyToggle('showEmail')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Mostrar estatísticas</h4>
                  <p className="text-xs text-muted-foreground">Estatísticas visíveis no seu perfil</p>
                </div>
                <Checkbox
                  checked={privacy?.showStats}
                  onChange={() => handlePrivacyToggle('showStats')}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-card-foreground">Permitir convites</h4>
                  <p className="text-xs text-muted-foreground">Outros podem te convidar para peladas</p>
                </div>
                <Checkbox
                  checked={privacy?.allowInvites}
                  onChange={() => handlePrivacyToggle('allowInvites')}
                />
              </div>
            </div>
            
            <div className="pt-4 border-t border-border">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="Shield" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-card-foreground text-sm">Dados Protegidos</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Seus dados são criptografados e nunca compartilhados com terceiros.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Methods */}
        {activeSection === 'payment' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-card-foreground">
                Métodos de Pagamento
              </span>
              <Button
                variant="ghost"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                iconSize={14}
                className="text-primary"
              >
                Adicionar
              </Button>
            </div>
            
            <div className="space-y-3">
              {paymentMethods?.map((method) => (
                <div
                  key={method?.id}
                  className={`border rounded-lg p-3 transition-standard ${
                    method?.isDefault 
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      method?.isDefault ? 'bg-primary/10 text-primary' : 'bg-muted/50 text-muted-foreground'
                    }`}>
                      <Icon name={getPaymentIcon(method?.type)} size={16} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-card-foreground text-sm">
                          {method?.name}
                        </span>
                        {method?.isDefault && (
                          <span className="px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                            Padrão
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {method?.identifier}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      {!method?.isDefault && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefaultPayment(method?.id)}
                          className="text-xs"
                        >
                          Definir Padrão
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreVertical"
                        className="text-muted-foreground"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-4 border-t border-border">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="Lock" size={16} className="text-success flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-card-foreground text-sm">Pagamentos Seguros</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Utilizamos criptografia de ponta e não armazenamos dados do cartão.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSettings;